import React, { useState, useEffect, useMemo } from 'react';
import { NavLink, Link } from 'react-router-dom';
import './food.css';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const FOOD_LOG_URL = SUPABASE_URL
  ? `${SUPABASE_URL}/rest/v1/food_log?order=date.desc`
  : '/food-log.json';

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const WEEKDAYS = 5;

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function mondayOf(d) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - dow);
  return x;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function weekDates(monday) {
  return Array.from({ length: WEEKDAYS }, (_, i) => addDays(monday, i));
}
function fmtDayNum(d) {
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}
function fmtWeekRange(monday) {
  const friday = addDays(monday, WEEKDAYS - 1);
  const sameMonth = monday.getMonth() === friday.getMonth();
  const sameYear = monday.getFullYear() === friday.getFullYear();
  const left = sameMonth
    ? `${monday.getDate()}`
    : `${monday.getDate()} ${MONTHS[monday.getMonth()]}${sameYear ? '' : ' ' + monday.getFullYear()}`;
  const right = `${friday.getDate()} ${MONTHS[friday.getMonth()]} ${friday.getFullYear()}`;
  return `${left} – ${right}`;
}
function monthKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function fmtMonth(d) {
  return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

// Which /lunch layout to show. "grid" (Instagram-style photo wall) and
// "list" (detailed rows) both show every logged day, newest first;
// "week" is the original paged Mon–Fri view with the summary strip.
const VIEW_KEY = 'lunch_view';
const VIEWS = ['grid', 'list', 'week'];
function readView() {
  try {
    const v = localStorage.getItem(VIEW_KEY);
    if (VIEWS.includes(v)) return v;
    if (v === 'feed') return 'list'; // migrate the earlier two-view scheme
  } catch (_) { /* private mode */ }
  return 'grid';
}
function foodThumb(src, w = 500) {
  if (!src) return '';
  if (src.includes('res.cloudinary.com')) return src.replace('/upload/', `/upload/f_auto,q_auto,w_${w},h_${w},c_fill/`);
  return src;
}
function foodFull(src) {
  if (!src) return '';
  if (src.includes('res.cloudinary.com')) return src.replace('/upload/', '/upload/f_auto,q_auto,w_1800/');
  return src;
}

// ── Shared chrome ─────────────────────────────────────────

const SECTIONS = [
  { key: 'writing', label: 'Writing', href: '/' },
  { key: 'photos', label: 'Map', href: '/photos' },
  { key: 'lunch', label: 'Lunch', href: '/lunch' },
  { key: 'resume', label: 'Résumé', href: '/resume' },
];

function SiteNav() {
  return (
    <nav className="site-nav" aria-label="Sections">
      {SECTIONS.map((s, i) => (
        <React.Fragment key={s.key}>
          {i > 0 ? <span className="site-nav-sep" aria-hidden="true">·</span> : null}
          <NavLink
            to={s.href}
            end={s.href === '/'}
            className={({ isActive }) => 'site-nav-link' + (isActive ? ' active' : '')}
          >
            {s.label}
          </NavLink>
        </React.Fragment>
      ))}
    </nav>
  );
}

function ThemeButton({ theme, onToggle }) {
  return (
    <button className="theme-toggle" onClick={onToggle} aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
      {theme === 'dark' ? (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round">
          <circle cx="8" cy="8" r="3" />
          <path d="M8 1.5v1.5M8 13v1.5M1.5 8H3M13 8h1.5M3.3 3.3l1 1M11.7 11.7l1 1M3.3 12.7l1-1M11.7 4.3l1-1" />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round">
          <path d="M13.5 9.5A6 6 0 1 1 6.5 2.5a5 5 0 0 0 7 7z" />
        </svg>
      )}
    </button>
  );
}

function Header({ theme, onToggleTheme }) {
  return (
    <header className="site-header">
      <Link className="wordmark" to="/" aria-label="Home">
        sandeep<span className="wordmark-dot">.</span>
      </Link>
      <div className="site-nav-wrap">
        <SiteNav />
      </div>
      <ThemeButton theme={theme} onToggle={onToggleTheme} />
    </header>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div className="footer-row">
        <a href="https://github.com/sandyjswl" target="_blank" rel="noopener noreferrer">GitHub</a>
        <a href="https://www.linkedin.com/in/sandyjswl/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
        <a href="https://www.instagram.com/sandyjswl" target="_blank" rel="noopener noreferrer">Instagram</a>
        <Link to="/photos">Map</Link>
        <a href="mailto:sandeepjswl123@gmail.com">Email</a>
      </div>
    </footer>
  );
}

// ── Summary strip ─────────────────────────────────────────

function weekStats(entries) {
  const logged = entries.filter(Boolean);
  if (!logged.length) return null;
  const totalCals = logged.reduce((s, e) => s + (e.calories || 0), 0);
  const avg = Math.round(totalCals / logged.length);
  const totalProt = logged.reduce((s, e) => s + ((e.macros && e.macros.protein) || 0), 0);
  const avgProt = Math.round(totalProt / logged.length);
  const counts = {};
  logged.forEach(e => { if (e.cuisine) counts[e.cuisine] = (counts[e.cuisine] || 0) + 1; });
  let topCuisine = '—'; let max = 0;
  Object.entries(counts).forEach(([k, v]) => { if (v > max) { max = v; topCuisine = k; } });
  return { totalCals, avg, avgProt, topCuisine, days: logged.length };
}

function Summary({ entries, prevEntries, prevEntries2 }) {
  const stats = useMemo(() => weekStats(entries), [entries]);
  const prevStats = useMemo(() => weekStats(prevEntries || []), [prevEntries]);
  const prev2Stats = useMemo(() => weekStats(prevEntries2 || []), [prevEntries2]);

  if (!stats) return null;

  function trend(diff) {
    if (!prevStats || diff === 0) return null;
    const arrow = diff > 0 ? '▲' : '▼';
    return `${arrow} ${Math.abs(diff).toLocaleString()} vs last wk`;
  }
  const calTrend = trend(stats.avg - (prevStats ? prevStats.avg : 0));
  const protTrend = trend(stats.avgProt - (prevStats ? prevStats.avgProt : 0));
  const cuisineStreak = stats.topCuisine !== '—' && prevStats && prev2Stats &&
    prevStats.topCuisine === stats.topCuisine && prev2Stats.topCuisine === stats.topCuisine;

  return (
    <div className="summary">
      <div className="sum-cell">
        <div className="sum-num">{stats.days}<span className="sum-unit">/{WEEKDAYS}</span></div>
        <div className="sum-label">days logged</div>
      </div>
      <div className="sum-cell">
        <div className="sum-num">{stats.avg.toLocaleString()}</div>
        <div className="sum-label">avg kcal / day</div>
        {calTrend ? <div className="sum-trend">{calTrend}</div> : null}
      </div>
      <div className="sum-cell">
        <div className="sum-num sum-num-prot">{stats.avgProt}<span className="sum-unit">g</span></div>
        <div className="sum-label">avg protein / day</div>
        {protTrend ? <div className="sum-trend">{protTrend}</div> : null}
      </div>
      <div className="sum-cell">
        <div className="sum-num sum-num-text">{stats.topCuisine}</div>
        <div className="sum-label">most had</div>
        {cuisineStreak ? <div className="sum-trend">3 weeks running</div> : null}
      </div>
    </div>
  );
}

// ── Consistency strip ──────────────────────────────────────
// Last 15 weekdays (3 weeks), independent of the paged week: a quiet
// at-a-glance "how regularly am I logging lately" indicator.

function Consistency({ dates, byDate }) {
  return (
    <div className="consistency">
      <span className="cst-label">Last 3 weeks</span>
      <div className="cst-dots">
        {dates.map((d) => {
          const logged = !!byDate[ymd(d)];
          const dayName = DAY_NAMES[(d.getDay() + 6) % 7];
          return (
            <span
              key={ymd(d)}
              className={'cst-dot' + (logged ? ' cst-dot-on' : '')}
              title={`${dayName} ${fmtDayNum(d)}${logged ? '' : ' — not logged'}`}
            />
          );
        })}
      </div>
    </div>
  );
}

// ── Macro bar ─────────────────────────────────────────────

function MacroBar({ macros }) {
  if (!macros) return null;
  const p = macros.protein || 0, c = macros.carbs || 0, f = macros.fat || 0;
  const pc = p * 4, cc = c * 4, fc = f * 9;
  const tot = pc + cc + fc || 1;
  return (
    <div className="macros">
      <div className="macro-bar" role="img" aria-label={`Protein ${p}g, carbs ${c}g, fat ${f}g`}>
        <span className="mb mb-p" style={{ width: `${(pc/tot)*100}%` }} />
        <span className="mb mb-c" style={{ width: `${(cc/tot)*100}%` }} />
        <span className="mb mb-f" style={{ width: `${(fc/tot)*100}%` }} />
      </div>
      <div className="macro-legend">
        <span className="ml"><span className="dot dot-p" />P {p}g</span>
        <span className="ml"><span className="dot dot-c" />C {c}g</span>
        <span className="ml"><span className="dot dot-f" />F {f}g</span>
      </div>
    </div>
  );
}

// ── Day row ───────────────────────────────────────────────

function DayRow({ date, entry, isToday, onOpenPhoto }) {
  const dow = (date.getDay() + 6) % 7;
  const dayName = DAY_NAMES[dow];
  const protein = entry && entry.macros ? (entry.macros.protein || 0) : 0;

  return (
    <li className={'lunch-row' + (entry ? '' : ' lunch-row-empty') + (isToday ? ' lunch-today' : '')}>
      {entry ? (
        <button className="lunch-photo" onClick={onOpenPhoto} aria-label={`${dayName} lunch photo`}>
          <img className="lunch-photo-img" src={foodThumb(entry.photo, 500)} alt="" loading="lazy" />
          {entry.cuisine ? <span className="day-cuisine">{entry.cuisine}</span> : null}
        </button>
      ) : (
        <div className="lunch-photo lunch-photo-empty" aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 3v7a2 2 0 0 0 2 2h0a2 2 0 0 0 2-2V3" />
            <path d="M9 12v9" />
            <path d="M17 3c-1.1 0-2 1.5-2 4s.9 4 2 4v9" />
          </svg>
        </div>
      )}

      <div className="lunch-body">
        <div className="lunch-dayline">
          <span className="ld-name">{dayName}</span>
          <span className="ld-date">{fmtDayNum(date)}</span>
          {isToday ? <span className="ld-today">Today</span> : null}
          {entry ? (
            <span className="lunch-stats">
              <span className="ls-cal">{(entry.calories || 0).toLocaleString()}<span className="ls-u">kcal</span></span>
              <span className="ls-sep" aria-hidden="true">·</span>
              <span className="ls-prot">{protein}g<span className="ls-u">protein</span></span>
            </span>
          ) : null}
        </div>

        {entry ? (
          <>
            <p className="lunch-items">
              {entry.items.map((it, i) => (
                <span key={i} className="li-item">
                  {it}{i < entry.items.length - 1 ? <span className="li-sep" aria-hidden="true">·</span> : null}
                </span>
              ))}
            </p>
            <MacroBar macros={entry.macros} />
          </>
        ) : (
          <div className="lunch-noentry">Not logged</div>
        )}
      </div>
    </li>
  );
}

// ── Loading skeleton ──────────────────────────────────────
// Mirrors the real layout (summary + 5 rows) so the page doesn't jump
// when data arrives; blocks pulse in place of numbers/photos.

function SkeletonSummary() {
  return (
    <div className="summary skeleton-summary" aria-hidden="true">
      {Array.from({ length: 4 }).map((_, i) => (
        <div className="sum-cell" key={i}>
          <span className="skel-text skel-sum-num" />
          <span className="skel-text skel-sum-label" />
        </div>
      ))}
    </div>
  );
}

function SkeletonFeed() {
  return (
    <ol className="lunch-feed skeleton-feed" aria-hidden="true">
      {Array.from({ length: 5 }).map((_, i) => (
        <li className="lunch-row skel-row" key={i} style={{ animationDelay: `${i * 90}ms` }}>
          <div className="lunch-photo skel" />
          <div className="lunch-body">
            <div className="lunch-dayline">
              <span className="skel-text skel-name" />
              <span className="skel-text skel-date" />
            </div>
            <span className="skel-text skel-items" />
            <span className="skel-text skel-items skel-items-2" />
            <span className="skel-text skel-macro" />
          </div>
        </li>
      ))}
    </ol>
  );
}

// ── Week nav ──────────────────────────────────────────────

function WeekNav({ monday, onPrev, onNext, canNext }) {
  return (
    <div className="week-nav">
      <button className="wn-btn" onClick={onPrev} aria-label="Previous week">
        <span className="wn-arrow">←</span>
        <span className="wn-txt">Prev</span>
      </button>
      <h2 className="week-range">{fmtWeekRange(monday)}</h2>
      <button className="wn-btn" onClick={onNext} disabled={!canNext} aria-label="Next week">
        <span className="wn-txt">Next</span>
        <span className="wn-arrow">→</span>
      </button>
    </div>
  );
}

// ── Lightbox ──────────────────────────────────────────────

function Lightbox({ entry, date, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const dow = (date.getDay() + 6) % 7;
  return (
    <div className="lightbox" role="dialog" aria-modal="true">
      <div className="lightbox-backdrop" onClick={onClose} />
      <button className="lb-close" onClick={onClose} aria-label="Close">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <path d="M5 5l10 10M15 5L5 15" />
        </svg>
      </button>
      <div className="lb-stage">
        <img className="lb-image" src={foodFull(entry.photo)} alt="" />
      </div>
      <div className="lb-caption">
        <div className="lb-cap-line">
          <span className="lb-loc">{DAY_NAMES[dow]}, {fmtDayNum(date)}</span>
          {entry.cuisine && <><span className="lb-dot"> · </span><span>{entry.cuisine}</span></>}
          <span className="lb-dot"> · </span>
          <span className="lb-date">{(entry.calories || 0).toLocaleString()} kcal</span>
        </div>
        <div className="lb-counter">{entry.items.join(' · ')}</div>
      </div>
    </div>
  );
}

// ── View switch ───────────────────────────────────────────

const VIEW_LABELS = { grid: 'Grid', list: 'List', week: 'Weekly' };

function ViewSwitch({ view, onChange }) {
  return (
    <div className="lunch-viewswitch" role="tablist" aria-label="Lunch view">
      {VIEWS.map((v) => (
        <button
          key={v}
          type="button"
          role="tab"
          aria-selected={view === v}
          className={'lvs-btn' + (view === v ? ' lvs-btn-on' : '')}
          onClick={() => onChange(v)}
        >
          {VIEW_LABELS[v]}
        </button>
      ))}
    </div>
  );
}

// ── Feed view (every logged day, grouped by month) ────────
// layout: "grid" = Instagram-style photo wall, "list" = detailed rows.

function FeedView({ log, today, onOpenPhoto, layout }) {
  const blocks = useMemo(() => {
    const entries = (log || [])
      .filter((e) => e && e.date)
      .slice()
      .sort((a, b) => b.date.localeCompare(a.date));
    const out = [];
    let key = null;
    entries.forEach((entry) => {
      const date = new Date(entry.date + 'T00:00:00');
      const mk = monthKey(date);
      if (mk !== key) {
        key = mk;
        out.push({ key: mk, label: fmtMonth(date), rows: [] });
      }
      out[out.length - 1].rows.push({ entry, date });
    });
    return out;
  }, [log]);

  if (!blocks.length) {
    return <p className="lunch-empty">Nothing logged yet.</p>;
  }

  return (
    <div className="lunch-allfeed">
      {blocks.map((block) => {
        const stats = weekStats(block.rows.map((r) => r.entry));
        return (
          <section className="lunch-month" key={block.key}>
            <div className="lunch-month-head">
              <h2 className="lunch-month-name">{block.label}</h2>
              {stats && (
                <span className="lunch-month-stat">
                  {stats.days} day{stats.days === 1 ? '' : 's'}
                  <span className="lm-dot" aria-hidden="true"> · </span>
                  {stats.avg.toLocaleString()} kcal
                  <span className="lm-dot" aria-hidden="true"> · </span>
                  {stats.avgProt}g protein
                </span>
              )}
            </div>
            {layout === 'grid' ? (
              <ol className="lunch-grid">
                {block.rows.map(({ entry, date }) => (
                  <li key={entry.date}>
                    <button
                      type="button"
                      className={'lunch-tile' + (ymd(date) === ymd(today) ? ' lunch-tile-today' : '')}
                      onClick={() => onOpenPhoto({ entry, date })}
                      aria-label={`${fmtDayNum(date)} — ${(entry.calories || 0).toLocaleString()} kcal`}
                    >
                      <img className="lunch-tile-img" src={foodThumb(entry.photo, 500)} alt="" loading="lazy" />
                      <span className="lunch-tile-meta">
                        {(entry.calories || 0).toLocaleString()} kcal
                        {entry.macros && entry.macros.protein != null
                          ? <><span aria-hidden="true"> · </span>{entry.macros.protein}g</>
                          : null}
                      </span>
                    </button>
                  </li>
                ))}
              </ol>
            ) : (
              <ol className="lunch-feed">
                {block.rows.map(({ entry, date }) => (
                  <DayRow
                    key={entry.date}
                    date={date}
                    entry={entry}
                    isToday={ymd(date) === ymd(today)}
                    onOpenPhoto={() => onOpenPhoto({ entry, date })}
                  />
                ))}
              </ol>
            )}
          </section>
        );
      })}
    </div>
  );
}

// ── Main component ────────────────────────────────────────

export default function FoodPage({ theme, onToggleTheme }) {
  const [log, setLog] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const headers = SUPABASE_URL
      ? { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` }
      : {};
    fetch(FOOD_LOG_URL, { cache: 'no-cache', headers })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(data => { if (!cancelled) setLog(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setLog([]); });
    return () => { cancelled = true; };
  }, []);

  const byDate = useMemo(() => {
    const m = {};
    (log || []).forEach(e => { m[e.date] = e; });
    return m;
  }, [log]);

  const today = new Date();
  const thisMonday = mondayOf(today);
  const [monday, setMonday] = useState(thisMonday);
  const [lightbox, setLightbox] = useState(null);
  const [view, setView] = useState(readView);

  useEffect(() => {
    try { localStorage.setItem(VIEW_KEY, view); } catch (_) { /* private mode */ }
  }, [view]);
  const dates = weekDates(monday);
  const entries = dates.map(d => byDate[ymd(d)] || null);
  const canNext = ymd(monday) < ymd(thisMonday);

  // previous two weeks' entries, for the trend + cuisine-streak reads in Summary
  const prevMonday = addDays(monday, -7);
  const prevMonday2 = addDays(monday, -14);
  const prevEntries = weekDates(prevMonday).map(d => byDate[ymd(d)] || null);
  const prevEntries2 = weekDates(prevMonday2).map(d => byDate[ymd(d)] || null);

  // last 15 weekdays (3 weeks) ending today, for the consistency strip
  const consistencyDates = [];
  {
    let d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    while (consistencyDates.length < 15) {
      const dow = (d.getDay() + 6) % 7;
      if (dow < 5) consistencyDates.unshift(new Date(d));
      d = addDays(d, -1);
    }
  }

  return (
    <div className="page page-food">
      <Header theme={theme} onToggleTheme={onToggleTheme} />
      <main className="main-food">
        <section className="food-intro">
          <h1 className="food-title">Lunch</h1>
          <p className="food-sub">
            A running log of the catered lunch at the office. One photo a day,
            calories and macros worked out by Claude. Mostly a way to keep myself honest.
          </p>
        </section>

        <ViewSwitch view={view} onChange={setView} />

        {log === null ? (
          view === 'week' ? (
            <>
              <SkeletonSummary />
              <SkeletonFeed />
            </>
          ) : (
            <SkeletonFeed />
          )
        ) : view !== 'week' ? (
          <FeedView log={log} today={today} onOpenPhoto={setLightbox} layout={view} />
        ) : (
          <>
            <Consistency dates={consistencyDates} byDate={byDate} />
            <WeekNav
              monday={monday}
              onPrev={() => setMonday(m => addDays(m, -7))}
              onNext={() => { if (canNext) setMonday(m => addDays(m, 7)); }}
              canNext={canNext}
            />
            <Summary entries={entries} prevEntries={prevEntries} prevEntries2={prevEntries2} />
            <ol className="lunch-feed">
              {dates.map((d, i) => ({ d, entry: entries[i] })).map(({ d, entry }) => (
                <DayRow
                  key={ymd(d)}
                  date={d}
                  entry={entry}
                  isToday={ymd(d) === ymd(today)}
                  onOpenPhoto={() => entry && setLightbox({ entry, date: d })}
                />
              ))}
            </ol>
          </>
        )}
      </main>
      <Footer />
      {lightbox && <Lightbox entry={lightbox.entry} date={lightbox.date} onClose={() => setLightbox(null)} />}
    </div>
  );
}
